/**
 * Pure key → tracker action mapping.
 *
 * Mirrors the FastTracker 2 / FamiTracker convention:
 *   Lower octave row: Z S X D C V G B H N J M  →  C C# D D# E F F# G G# A A# B
 *   Upper octave row: Q 2 W 3 E R 5 T 6 Y 7 U  →  C C# D D# E F F# G G# A A# B (+1 octave)
 *
 * Navigation: arrow keys (cell-by-cell), PageUp/Down (page-jump).
 * Cell edits: comma → note-off, Backspace/Delete → clear cell.
 * Octave: + / - shifts the current octave by ±1.
 * Transport: Space toggles play/stop.
 *
 * This module is pure — no React, no DOM. The hook in
 * `hooks/useTrackerKeyboard.ts` wires it to the live state.
 */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/** Semitone offset (0..11) for each lower-octave key. */
const LOWER_OCTAVE: Record<string, number> = {
  z: 0,
  s: 1,
  x: 2,
  d: 3,
  c: 4,
  v: 5,
  g: 6,
  b: 7,
  h: 8,
  n: 9,
  j: 10,
  m: 11,
};

/** Semitone offset (0..11) for each upper-octave key. */
const UPPER_OCTAVE: Record<string, number> = {
  q: 0,
  '2': 1,
  w: 2,
  '3': 3,
  e: 4,
  r: 5,
  '5': 6,
  t: 7,
  '6': 8,
  y: 9,
  '7': 10,
  u: 11,
};

/** Format a note name + octave as either "C-5" (natural) or "C#5" (sharp). */
export function formatNote(semitone: number, octave: number): string {
  const name = NOTE_NAMES[semitone];
  if (!name) throw new Error(`semitone out of range: ${semitone}`);
  return name.includes('#') ? `${name}${octave}` : `${name}-${octave}`;
}

export type KeyAction =
  | { type: 'note'; note: string }
  | { type: 'noteOff' }
  | { type: 'clearCell' }
  | { type: 'moveCursor'; dRow: number; dChannel: number }
  | { type: 'octave'; delta: number }
  | { type: 'transport'; action: 'playStop' };

export interface KeyContext {
  octave: number;
}

/**
 * Translate a physical key event into a tracker action.
 * Returns null if the key has no mapping (consumer should pass it through).
 */
export function mapKeyToAction(key: string, ctx: KeyContext): KeyAction | null {
  const lower = key.toLowerCase();

  // Notes — lower octave (Z, S, X, ...) → current octave
  if (lower in LOWER_OCTAVE) {
    return { type: 'note', note: formatNote(LOWER_OCTAVE[lower]!, ctx.octave) };
  }
  // Notes — upper octave (Q, 2, W, ...) → current octave + 1
  if (lower in UPPER_OCTAVE) {
    return { type: 'note', note: formatNote(UPPER_OCTAVE[lower]!, ctx.octave + 1) };
  }

  // Non-character keys (use the raw key, not lowercased).
  switch (key) {
    case 'ArrowUp':
      return { type: 'moveCursor', dRow: -1, dChannel: 0 };
    case 'ArrowDown':
      return { type: 'moveCursor', dRow: 1, dChannel: 0 };
    case 'ArrowLeft':
      return { type: 'moveCursor', dRow: 0, dChannel: -1 };
    case 'ArrowRight':
      return { type: 'moveCursor', dRow: 0, dChannel: 1 };
    case 'PageUp':
      return { type: 'moveCursor', dRow: -16, dChannel: 0 };
    case 'PageDown':
      return { type: 'moveCursor', dRow: 16, dChannel: 0 };
    case 'Home':
      return { type: 'moveCursor', dRow: -9999, dChannel: 0 };
    case 'End':
      return { type: 'moveCursor', dRow: 9999, dChannel: 0 };
    case 'Backspace':
    case 'Delete':
      return { type: 'clearCell' };
    case ',':
      return { type: 'noteOff' };
    case '+':
    case '=':
      return { type: 'octave', delta: 1 };
    case '-':
      return { type: 'octave', delta: -1 };
    case ' ':
      return { type: 'transport', action: 'playStop' };
    default:
      return null;
  }
}

/** Stable identifiers for the five 2A03 channels in cursor-traversal order. */
export const CHANNEL_ORDER = ['pulse1', 'pulse2', 'triangle', 'noise', 'dpcm'] as const;
export type ChannelName = (typeof CHANNEL_ORDER)[number];
